import { Box, Typography } from '@/shared/ui';

import { useFlowchartImage } from '@/features/factsheet/api/queries';
import { getVersionHistoryField } from '@/features/factsheet/lib/versionHistory';
import type { FactsheetDetails } from '@/features/factsheet/types';
import Loader from '@/shared/ui/feedback/Spinner';

import RelatedCodeList from '../RelatedCodesTab/RelatedCodeList';

import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { Sx } from '@/shared/types/uiTypes';

interface FlowchartProps {
  flowchartFileId: string;
  boxStyles?: Sx;
}

const Flowchart = ({ flowchartFileId, boxStyles }: FlowchartProps) => {
  const { data: imageSrc, isError } = useFlowchartImage(flowchartFileId);

  if (isError) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...boxStyles
      }}
    >
      {imageSrc ? (
        <img
          alt='flowchart'
          src={imageSrc}
          style={{ width: '90%' }}
        />
      ) : (
        <Box sx={{ width: '100%', height: 250 }}>
          <Loader />
        </Box>
      )}
    </Box>
  );
};

interface FlowchartTabProps {
  factsheet: FactsheetDetails;
  openNewTab: (code: string) => void;
}

const FlowchartTab = ({ factsheet, openNewTab }: FlowchartTabProps) => {
  const parentsWithFlowcharts = getVersionHistoryField(factsheet, 'parentsWithFlowcharts') ?? [];
  const flowchartIds = getVersionHistoryField(factsheet, 'flowcharts') ?? [];

  // Filter out invalid flowchart IDs
  const validFlowchartIds = flowchartIds.filter(id => id && id !== 'false');
  const isCompact = useIsFactsheetCompact();
  // If we have valid flowcharts, display them
  if (validFlowchartIds.length > 0) {
    return (
      <Box>
        {validFlowchartIds.map((flowchartId, index) => (
          <Flowchart
            key={flowchartId}
            boxStyles={index > 0 ? { mt: 4 } : undefined}
            flowchartFileId={flowchartId}
          />
        ))}
      </Box>
    );
  }

  // No flowcharts - show parent flowchart references

  return (
    <Box>
      <Typography
        size={isCompact ? 14 : undefined}
        sx={{ fontWeight: 700, pb: 1 }}
      >
        Flowchart
      </Typography>
      <Typography
        size={isCompact ? 14 : undefined}
        sx={{ pb: 2 }}
      >
        None.
      </Typography>
      <RelatedCodeList
        openNewTab={openNewTab}
        relatedCodes={parentsWithFlowcharts}
        title='Parent Code Flowchart'
      />
    </Box>
  );
};

export default FlowchartTab;
